import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Grid, TextField, Switch,
  FormControlLabel, Alert, CircularProgress, Divider, Chip, Table, TableHead,
  TableBody, TableRow, TableCell, Paper,
} from '@mui/material';
import { Save, Send, MarkEmailRead, Refresh } from '@mui/icons-material';
import api from '../../services/api';

interface Setting {
  event_type: string;
  label: string;
  description: string;
  enabled: boolean;
  to_recipients: string[];
  cc_recipients: string[];
  bcc_recipients: string[];
  throttle_seconds: number;
  last_sent_at: string | null;
}

const joinList = (a: string[] | null) => (a || []).join(', ');
const splitList = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);

export const NotificationsPage: React.FC = () => {
  const [rows, setRows] = useState<Setting[]>([]);
  const [log, setLog] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/notifications/settings'), api.get('/notifications/log?limit=50')])
      .then(([s, l]) => { setRows(s.data || []); setLog(l.data || []); })
      .catch((e) => setError(e.response?.data?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setField = (i: number, field: keyof Setting, value: any) => {
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, [field]: value } : row)));
  };

  const save = async (row: Setting) => {
    setSaving(row.event_type); setSuccess(''); setError('');
    try {
      await api.put(`/notifications/settings/${row.event_type}`, {
        enabled: row.enabled,
        to_recipients: row.to_recipients,
        cc_recipients: row.cc_recipients,
        bcc_recipients: row.bcc_recipients,
        throttle_seconds: Number(row.throttle_seconds) || 0,
      });
      setSuccess(`Saved “${row.label}”.`);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Save failed');
    } finally { setSaving(''); }
  };

  const sendTest = async (row: Setting) => {
    setSaving('test:' + row.event_type); setSuccess(''); setError('');
    try {
      const { data } = await api.post(`/notifications/dispatch-test/${row.event_type}`);
      if (data?.ok) setSuccess(`Test sent for “${row.label}” to its configured recipients.`);
      else setSuccess(`Not sent (${data?.throttled ? 'throttled' : 'skipped/disabled'}).`);
      setTimeout(load, 800);
    } catch (e: any) {
      setError(e.response?.data?.message || 'Test failed');
    } finally { setSaving(''); }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <MarkEmailRead color="primary" />
        <Typography variant="h4" fontWeight={700}>Notifications</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose who receives each type of system email. Sent from <b>noreply@synergytalk.ae</b>.
        Separate multiple addresses with commas. Throttle = minimum seconds between emails for that event (0 = no limit).
      </Typography>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Grid container spacing={2}>
        {rows.map((row, i) => (
          <Grid item xs={12} md={6} key={row.event_type}>
            <Card variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">{row.label}</Typography>
                    <Chip size="small" label={row.event_type} sx={{ mt: 0.5 }} />
                  </Box>
                  <FormControlLabel
                    control={<Switch checked={row.enabled} onChange={(e) => setField(i, 'enabled', e.target.checked)} />}
                    label={row.enabled ? 'On' : 'Off'}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">{row.description}</Typography>
                <Divider sx={{ my: 1.5 }} />
                <TextField fullWidth size="small" label="To (comma-separated)" sx={{ mb: 1 }}
                  value={joinList(row.to_recipients)} onChange={(e) => setField(i, 'to_recipients', splitList(e.target.value))} />
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Cc" value={joinList(row.cc_recipients)}
                      onChange={(e) => setField(i, 'cc_recipients', splitList(e.target.value))} />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField fullWidth size="small" label="Bcc" value={joinList(row.bcc_recipients)}
                      onChange={(e) => setField(i, 'bcc_recipients', splitList(e.target.value))} />
                  </Grid>
                </Grid>
                <TextField size="small" type="number" label="Throttle (sec)" sx={{ mt: 1, width: 160 }}
                  value={row.throttle_seconds} onChange={(e) => setField(i, 'throttle_seconds', e.target.value)} />
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  <Button variant="contained" size="small" startIcon={<Save />}
                    disabled={saving === row.event_type} onClick={() => save(row)}>Save</Button>
                  <Button variant="outlined" size="small" startIcon={<Send />}
                    disabled={saving === 'test:' + row.event_type} onClick={() => sendTest(row)}>Send test</Button>
                  {row.last_sent_at && (
                    <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                      last: {new Date(row.last_sent_at).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 4, mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>Recent activity</Typography>
        <Button size="small" startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Box>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Time</TableCell><TableCell>Event</TableCell><TableCell>Recipients</TableCell>
              <TableCell>Status</TableCell><TableCell>Detail</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {log.length === 0 && <TableRow><TableCell colSpan={5}><i>No activity yet.</i></TableCell></TableRow>}
            {log.map((l) => (
              <TableRow key={l.id}>
                <TableCell>{new Date(l.created_at).toLocaleString()}</TableCell>
                <TableCell>{l.event_type}</TableCell>
                <TableCell>{l.recipients}</TableCell>
                <TableCell>
                  <Chip size="small" label={l.status}
                    color={l.status === 'sent' ? 'success' : l.status === 'failed' ? 'error' : 'default'} />
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: 12 }}>{l.error || ''}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};
